import { OrderedSet } from 'molstar/lib/mol-data/int';
import { Mat4 } from 'molstar/lib/mol-math/linear-algebra';
import { StructureElement, StructureProperties, Unit } from 'molstar/lib/mol-model/structure';
import { MolScriptBuilder as MS } from 'molstar/lib/mol-script/language/builder';
import { StateTransforms } from 'molstar/lib/mol-plugin-state/transforms';
import { Color } from 'molstar/lib/mol-util/color';
import { MarkerAction } from 'molstar/lib/mol-util/marker-action';
import { atomToPdbRow } from '../lib/pdbAssembly.js';
import { normalizePdb } from '../lib/structureText.js';

export { atomToPdbRow };

export const OneToThree = {
    A: 'ALA',
    R: 'ARG',
    N: 'ASN',
    D: 'ASP',
    C: 'CYS',
    E: 'GLU',
    Q: 'GLN',
    G: 'GLY',
    H: 'HIS',
    I: 'ILE',
    L: 'LEU',
    K: 'LYS',
    M: 'MET',
    F: 'PHE',
    P: 'PRO',
    S: 'SER',
    T: 'THR',
    W: 'TRP',
    Y: 'TYR',
    V: 'VAL',
    U: 'SEC',
    O: 'PYL',
    X: 'ALA',
};

const ThreeToOne = {};
for (const [one, three] of Object.entries(OneToThree)) {
    if (!ThreeToOne[three]) ThreeToOne[three] = one;
}

const CartoonQualityPresets = {
    viewer: {
        quality: 'highest',
        linearSegments: 18,
        radialSegments: 36,
    },
    thumbnail: {
        quality: 'higher',
        linearSegments: 10,
        radialSegments: 16,
    },
};

export async function loadStructureFromData(plugin, source, label = source?.label) {
    const format = source.format || 'pdb';
    const raw = await plugin.builders.data.rawData(
        { data: format === 'pdb' ? normalizePdb(source.data) : source.data, label },
        { state: { isGhost: true } },
    );
    const trajectory = await plugin.builders.structure.parseTrajectory(raw, format);
    const model = await plugin.builders.structure.createModel(trajectory);
    return plugin.builders.structure.createStructure(model, { name: 'model', params: {} });
}

// Structures without a chain column report a blank id; treat it as unnamed.
export function normalizeChainId(value) {
    return String(value ?? '').trim();
}

export function caChainIds(structureRef) {
    const structure = structureRef?.cell?.obj?.data || structureRef;
    const ids = new Set();
    if (!structure) return ids;

    const location = StructureElement.Location.create(structure);
    for (const unit of structure.units || []) {
        if (!Unit.isAtomic(unit)) continue;
        location.unit = unit;
        for (let index = 0; index < OrderedSet.size(unit.elements); index++) {
            location.element = OrderedSet.getAt(unit.elements, index);
            if (StructureProperties.atom.label_atom_id(location) !== 'CA') continue;
            const authChain = normalizeChainId(StructureProperties.chain.auth_asym_id(location));
            const labelChain = normalizeChainId(StructureProperties.chain.label_asym_id(location));
            ids.add(authChain || labelChain);
        }
    }
    return ids;
}

export function detectStructureFormat(raw) {
    const text = raw?.trimStart?.() || '';
    // A writer may lead with a comment, a data block or the loop itself; a PDB file has none of them.
    return text[0] === '#' || text.startsWith('data_') || text.startsWith('loop_')
        || text.startsWith('_atom_site.') ? 'mmcif' : 'pdb';
}

export function normalizedPdbSourceFromText(raw, label, options = {}) {
    const data = raw?.trimStart?.() || '';
    const format = detectStructureFormat(data);
    return {
        data: format === 'pdb' ? normalizePdb(data, options) : data,
        label,
        format,
    };
}

export function isValidLoci(loci) {
    return StructureElement.Loci.is(loci) && !StructureElement.Loci.isEmpty(loci);
}

export function lociFromExpression(structureRef, expression) {
    const structure = structureRef?.cell?.obj?.data || structureRef;
    if (!structure || !expression) return null;
    const loci = StructureElement.Loci.fromExpression(structure, expression);
    return StructureElement.Loci.isEmpty(loci) ? null : loci;
}

function residueInfoFromLocation(loc) {
    const authResidue = StructureProperties.residue.auth_seq_id(loc);
    const labelResidue = StructureProperties.residue.label_seq_id(loc);
    const threeLetter = StructureProperties.residue.label_comp_id(loc)
        || StructureProperties.residue.auth_comp_id(loc)
        || '';
    const authChain = normalizeChainId(StructureProperties.chain.auth_asym_id(loc));
    const labelChain = normalizeChainId(StructureProperties.chain.label_asym_id(loc));
    const chain = authChain || labelChain;

    return {
        chain,
        authChain,
        labelChain,
        residue: labelResidue || authResidue,
        authResidue,
        labelResidue,
        threeLetter,
        oneLetter: ThreeToOne[threeLetter] || '',
        residues: [labelResidue || authResidue],
    };
}

function structureElementLocationFromLoci(loci) {
    if (!isValidLoci(loci)) return null;
    const entry = loci.elements?.[0];
    if (!entry || OrderedSet.size(entry.indices) === 0) return null;

    const loc = StructureElement.Location.create(loci.structure);
    loc.unit = entry.unit;
    loc.element = OrderedSet.getAt(entry.indices, 0);
    return loc;
}

export function residueInfoFromLoci(loci) {
    const loc = structureElementLocationFromLoci(loci);
    return loc ? residueInfoFromLocation(loc) : null;
}

export function residueInfosFromLoci(loci) {
    if (!isValidLoci(loci)) return [];

    const residues = [];
    const seen = new Set();
    StructureElement.Loci.forEachLocation(loci, (loc) => {
        const residue = residueInfoFromLocation(loc);
        const key = [
            residue.authChain,
            residue.labelChain,
            residue.authResidue,
            residue.labelResidue,
        ].join(':');
        if (seen.has(key)) return;
        seen.add(key);
        residues.push(residue);
    });
    return residues;
}

export function parseCaCoordinates(value) {
    if (typeof value !== 'string' || !value) return [];
    const numbers = value.split(',').map(Number);
    const coordinates = [];
    for (let index = 0; index + 2 < numbers.length; index += 3) {
        if (numbers.slice(index, index + 3).every(Number.isFinite)) {
            coordinates.push(numbers.slice(index, index + 3));
        }
    }
    return coordinates;
}

export function nearestResidue(residues, [x, y, z]) {
    let nearest = null;
    let distanceSquared = Infinity;
    for (const residue of residues) {
        const dx = residue.x - x;
        const dy = residue.y - y;
        const dz = residue.z - z;
        const candidate = dx * dx + dy * dy + dz * dz;
        if (candidate < distanceSquared) {
            distanceSquared = candidate;
            nearest = residue;
        }
    }
    return nearest;
}

export function caResiduesByChain(structureRef) {
    const structure = structureRef?.cell?.obj?.data || structureRef;
    const residuesByChain = new Map();
    if (!structure) return residuesByChain;

    const seen = new Set();
    const location = StructureElement.Location.create(structure);
    for (const unit of structure.units || []) {
        if (!Unit.isAtomic(unit)) continue;
        location.unit = unit;
        for (let index = 0; index < OrderedSet.size(unit.elements); index++) {
            location.element = OrderedSet.getAt(unit.elements, index);
            if (StructureProperties.atom.label_atom_id(location) !== 'CA') continue;

            const authChain = normalizeChainId(StructureProperties.chain.auth_asym_id(location));
            const labelChain = normalizeChainId(StructureProperties.chain.label_asym_id(location));
            const authResidue = StructureProperties.residue.auth_seq_id(location);
            const labelResidue = StructureProperties.residue.label_seq_id(location);
            const identity = `${labelChain}:${authChain}:${labelResidue}:${authResidue}`;
            if (seen.has(identity)) continue;
            seen.add(identity);

            const residue = {
                chain: authChain || labelChain,
                authChain,
                labelChain,
                authResidue,
                labelResidue,
                x: StructureProperties.atom.x(location),
                y: StructureProperties.atom.y(location),
                z: StructureProperties.atom.z(location),
            };
            const chains = new Set([authChain, labelChain].filter(Boolean));
            if (chains.size === 0) chains.add('');
            for (const chain of chains) {
                if (!residuesByChain.has(chain)) residuesByChain.set(chain, []);
                residuesByChain.get(chain).push(residue);
            }
        }
    }
    return residuesByChain;
}

export function structuresMatch(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    return a.root === b.root && a.elementCount === b.elementCount;
}

export function focusCurrentLoci(plugin, current, options = {}) {
    if (isValidLoci(current?.loci)) {
        plugin.managers.camera.focusLoci(current.loci, {
            durationMs: 250,
            extraRadius: 6,
            minRadius: 3,
            ...options,
        });
    }
}

export function cartoonParams(overrides = {}) {
    const { qualityPreset = 'viewer', ...typeOverrides } = overrides;
    const quality = CartoonQualityPresets[qualityPreset] || CartoonQualityPresets.viewer;
    return {
        ...quality,
        ignoreLight: false,
        sizeFactor: 0.25,
        visuals: ['polymer-trace', 'polymer-gap'],
        helixProfile: 'rounded',
        nucleicProfile: 'rounded',
        material: {
            metalness: 0,
            roughness: 1,
            bumpiness: 0,
        },
        ...typeOverrides,
    };
}

export function ballAndStickParams(overrides = {}) {
    return {
        quality: 'higher',
        sizeFactor: 0.28,
        sizeAspectRatio: 0.7,
        aromaticBonds: true,
        material: {
            metalness: 0,
            roughness: 0.5,
            bumpiness: 0,
        },
        ...overrides,
    };
}

export function representationTypeParams(type, overrides = {}) {
    if (type === 'cartoon') return cartoonParams(overrides);
    if (type === 'ball-and-stick') return ballAndStickParams(overrides);
    return {
        quality: 'higher',
        ignoreLight: false,
        ...overrides,
    };
}

export async function addUniformRepresentation(plugin, structure, options) {
    const {
        label,
        expression = MS.struct.generator.all(),
        type = 'cartoon',
        color,
        typeParams = {},
        tag = `${label}-representation`,
        initialState,
    } = options;
    if (!expression) return null;

    const component = await plugin.builders.structure.tryCreateComponentFromExpression(
        structure,
        expression,
        label,
        { label },
    );
    if (!component) return null;

    return {
        component,
        representation: await plugin.builders.structure.representation.addRepresentation(component, {
            type,
            color: 'uniform',
            colorParams: { value: Color(color) },
            typeParams: representationTypeParams(type, typeParams),
        }, {
            tag,
            ...(initialState ? { initialState } : {}),
        }),
    };
}

export function chainExpression(chain) {
    const test = chainTest(chain);
    return test ? MS.struct.generator.atomGroups({
        'chain-test': test,
        'residue-test': residueAtomTest(),
    }) : null;
}

export function residueExpression(chain, residue) {
    const tests = {
        'residue-test': MS.core.logic.and([residueAtomTest(), residueEqTest(residue)]),
    };
    const chainExpr = chainTest(chain);
    if (chainExpr) tests['chain-test'] = chainExpr;
    return MS.struct.generator.atomGroups(tests);
}

export function mergeExpressions(expressions) {
    const valid = expressions.filter(Boolean);
    if (valid.length === 0) return null;
    if (valid.length === 1) return valid[0];
    return MS.struct.combinator.merge(valid.map(expression => MS.struct.modifier.union([expression])));
}

export function expressionForResidues(residues) {
    if (!residues?.length) return null;

    const groups = new Map();
    for (const residue of residues) {
        const useLabel = residue.labelChain && Number.isFinite(residue.labelResidue);
        const chainProperty = useLabel
            ? MS.struct.atomProperty.macromolecular.label_asym_id()
            : MS.struct.atomProperty.macromolecular.auth_asym_id();
        const residueProperty = useLabel
            ? MS.struct.atomProperty.macromolecular.label_seq_id()
            : MS.struct.atomProperty.macromolecular.auth_seq_id();
        const chainId = useLabel ? residue.labelChain : residue.authChain;
        const residueId = useLabel ? residue.labelResidue : residue.authResidue;
        if (!chainId || !Number.isFinite(residueId)) continue;

        const key = `${useLabel ? 'label' : 'auth'}:${chainId}`;
        if (!groups.has(key)) groups.set(key, { chainId, chainProperty, residueProperty, residues: [] });
        groups.get(key).residues.push(residueId);
    }

    return mergeExpressions(Array.from(groups.values()).flatMap(group => (
        residueRanges(group.residues.sort((a, b) => a - b))
            .map(([start, end]) => atomGroupExpressionForProperty(group.chainId, group.chainProperty, group.residueProperty, start, end))
    )));
}

export function residuesForMappedRange(map, start, end) {
    const residues = [];
    for (let position = start; position <= end; position++) {
        const residue = map.toStructure.get(position);
        if (residue) residues.push(residue);
    }
    return residues;
}

export function structureResidueKeys(residue, chain = null) {
    const keys = [];
    if (chain) {
        if (Number.isFinite(residue.labelResidue)) keys.push(`${chain}:label:${residue.labelResidue}`);
        if (Number.isFinite(residue.authResidue)) keys.push(`${chain}:auth:${residue.authResidue}`);
        return keys;
    }
    if (Number.isFinite(residue.labelResidue)) {
        keys.push(`${residue.labelChain || ''}:label:${residue.labelResidue}`);
    }
    if (Number.isFinite(residue.authResidue)) {
        keys.push(`${residue.authChain || ''}:auth:${residue.authResidue}`);
    }
    return keys;
}

export function residueRanges(residues) {
    if (residues.length === 0) return [];
    const ranges = [];
    let start = residues[0];
    let prev = start;
    for (let i = 1; i < residues.length; i++) {
        if (residues[i] === prev + 1) {
            prev = residues[i];
            continue;
        }
        ranges.push([start, prev]);
        start = residues[i];
        prev = residues[i];
    }
    ranges.push([start, prev]);
    return ranges;
}

function residueRangeTest(property, start, end) {
    const { and } = MS.core.logic;
    const { gre, lte } = MS.core.rel;
    const tests = [];
    if (Number.isFinite(start)) tests.push(gre([property, start]));
    if (Number.isFinite(end)) tests.push(lte([property, end]));
    return tests.length === 1 ? tests[0] : and(tests);
}

function chainTest(chain) {
    if (!chain) return null;
    const { or } = MS.core.logic;
    const { eq } = MS.core.rel;
    const { macromolecular } = MS.struct.atomProperty;
    return or([
        eq([macromolecular.auth_asym_id(), chain]),
        eq([macromolecular.label_asym_id(), chain]),
    ]);
}

function residueEqTest(residue) {
    const { or } = MS.core.logic;
    const { eq } = MS.core.rel;
    const { macromolecular } = MS.struct.atomProperty;
    return or([
        eq([macromolecular.auth_seq_id(), residue]),
        eq([macromolecular.label_seq_id(), residue]),
    ]);
}

function residueAtomTest() {
    return MS.core.logic.not([MS.struct.atomProperty.macromolecular.isHet()]);
}

function atomGroupExpressionForProperty(chain, chainProperty, residueProperty, start, end) {
    const { eq } = MS.core.rel;
    return MS.struct.generator.atomGroups({
        'chain-test': eq([chainProperty, chain]),
        'residue-test': residueRangeTest(residueProperty, start, end),
    });
}

function structureRef(structure) {
    return structure?.ref || structure;
}

export function representationRef(item) {
    return item?.representation?.ref
        || item?.representation?.cell?.transform?.ref
        || item?.representation?.cell?.ref
        || item?.ref
        || item?.cell?.transform?.ref
        || null;
}

export function representationObject(item) {
    return item?.representation?.cell?.obj?.data?.repr
        || item?.representation?.obj?.data?.repr
        || item?.representation?.data?.repr
        || item?.repr
        || null;
}

export function componentStructure(item) {
    return item?.component?.cell?.obj?.data
        || item?.component?.obj?.data
        || item?.component?.data
        || null;
}

export function componentRef(item) {
    return item?.component?.ref
        || item?.component?.cell?.transform?.ref
        || item?.component?.cell?.ref
        || null;
}

export async function deleteComponent(plugin, item) {
    const ref = componentRef(item);
    if (ref) await plugin.state.data.build().delete(ref).commit();
}

export function addRepresentationToSet(set, item, options = {}) {
    const repr = representationObject(item);
    if (repr) set?.add(repr);
    if (options.includeComponent) {
        const component = componentStructure(item);
        if (component) set?.add(component);
    }
}

export function setRepresentationNonPickable(item) {
    representationObject(item)?.setState?.({
        pickable: false,
        markerActions: MarkerAction.None,
    });
}

export async function transformStructureConformation(plugin, structure, matrix, options = {}) {
    if (!matrix) return structure;
    const params = {
        transform: {
            name: 'matrix',
            params: { data: matrix, transpose: false },
        },
    };
    const stateOptions = options.tags ? { tags: options.tags } : undefined;
    const builder = plugin.state.data.build().to(structureRef(structure));
    const update = options.method === 'apply'
        ? builder.apply(StateTransforms.Model.TransformStructureConformation, params, stateOptions)
        : builder.insert(StateTransforms.Model.TransformStructureConformation, params, stateOptions);
    return update.commit();
}

export function mat4FromRotationTranslation(t, u) {
    return Mat4.ofRows([
        [u[0][0], u[0][1], u[0][2], t[0]],
        [u[1][0], u[1][1], u[1][2], t[1]],
        [u[2][0], u[2][1], u[2][2], t[2]],
        [0, 0, 0, 1],
    ]);
}

function matrixParamsFromCommaStrings(tmat, umat) {
    const t = String(tmat || '').split(',').map(Number);
    const u = String(umat || '').split(',').map(Number);
    if (t.length < 3 || u.length < 9 || t.some(Number.isNaN) || u.some(Number.isNaN)) return null;
    return {
        t,
        u: [
            [u[0], u[1], u[2]],
            [u[3], u[4], u[5]],
            [u[6], u[7], u[8]],
        ],
    };
}

export function mat4FromCommaStrings(tmat, umat) {
    const params = matrixParamsFromCommaStrings(tmat, umat);
    return params ? mat4FromRotationTranslation(params.t, params.u) : null;
}

export function mockPDB(ca, seq, chain, start = 1) {
    const atoms = ca.split(',');
    const pdb = [];
    let j = 1;
    for (let i = 0; i < atoms.length; i += 3, j++) {
        const resno = start + j - 1;
        const [x, y, z] = atoms.slice(i, i + 3).map(element => Number.parseFloat(element));
        const residue = seq !== '' && atoms.length / 3 === seq.length ? seq[i / 3] : 'A';
        pdb.push(atomToPdbRow({
            serial: j,
            atomName: 'CA',
            resName: OneToThree[residue] || 'ALA',
            chain,
            resno,
            x,
            y,
            z,
            element: 'C',
        }));
    }
    return pdb.join('\n');
}
